import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createGuild, fetchGuilds, fetchMyGuilds, joinGuild } from '../api/guilds';
import { fetchServers } from '../api/servers';
import { useAuth } from '../auth/AuthContext';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { getActiveServerSlug, serverDisplayName, serverGuildPath } from '../utils/siteContext';

const PAGE_SIZE = 50;

function guildSearchText(guild: { name: string; description?: string | null }) {
  return `${guild.name} ${guild.description ?? ''}`.toLowerCase();
}

export function GuildsPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const activeServerSlug = getActiveServerSlug();
  const activeServerName = serverDisplayName(activeServerSlug);
  const guildsQuery = useQuery({
    queryKey: ['guilds', activeServerSlug],
    queryFn: () => fetchGuilds({ serverSlug: activeServerSlug })
  });
  const myGuildsQuery = useQuery({ queryKey: ['me', 'guilds'], queryFn: fetchMyGuilds, enabled: auth.isAuthenticated });
  const serversQuery = useQuery({ queryKey: ['servers'], queryFn: fetchServers });

  const activeServer = useMemo(
    () => (serversQuery.data ?? []).find((server) => server.slug === activeServerSlug) ?? null,
    [activeServerSlug, serversQuery.data]
  );

  const [createForm, setCreateForm] = useState({ serverId: '', name: '', description: '' });
  const [joinCode, setJoinCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [guildSearch, setGuildSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (activeServer?.id) {
      setCreateForm((form) => ({ ...form, serverId: String(activeServer.id) }));
    }
  }, [activeServer?.id]);

  const filteredGuilds = useMemo(() => {
    const search = guildSearch.trim().toLowerCase();
    const guilds = guildsQuery.data ?? [];

    const filtered = search
      ? guilds.filter((guild) => guildSearchText(guild).includes(search))
      : guilds;

    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [guildSearch, guildsQuery.data]);

  const totalPages = Math.max(1, Math.ceil(filteredGuilds.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleGuilds = filteredGuilds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [guildSearch, activeServerSlug]);

  const createMutation = useMutation({
    mutationFn: () => createGuild({
      serverId: Number(createForm.serverId),
      name: createForm.name,
      description: createForm.description || undefined
    }),
    onSuccess: async (guild) => {
      setCreateForm({ serverId: activeServer?.id ? String(activeServer.id) : '', name: '', description: '' });
      setMessage(`Created ${guild.name}. You are the owner.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['guilds'] }),
        queryClient.invalidateQueries({ queryKey: ['me', 'guilds'] })
      ]);
    }
  });

  const joinMutation = useMutation({
    mutationFn: () => joinGuild({ code: joinCode }),
    onSuccess: async ({ guild }) => {
      setJoinCode('');
      setMessage(`Joined ${guild.name}.`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['guilds'] }),
        queryClient.invalidateQueries({ queryKey: ['me', 'guilds'] })
      ]);
    }
  });

  function handleCreateSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    createMutation.mutate();
  }

  function handleJoinSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    joinMutation.mutate();
  }

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">Guilds</h1>
          <span className="page-hero__server d-block">{activeServerName}</span>
        </div>
      </section>

      <div className="content-wrapper py-4">
        {auth.isAuthenticated ? (
          <div className="row g-4 mb-4">
            <div className="col-12 col-xl-5">
              <div className="card rankings-card h-100">
                <div className="card-body p-3">
                  <h2 className="h5 mb-3">My Guilds</h2>
                  {myGuildsQuery.isLoading ? <LoadingState label="Loading memberships" /> : null}
                  {myGuildsQuery.isError ? <ErrorState title="Could not load your guilds" error={myGuildsQuery.error} /> : null}
                  {myGuildsQuery.data ? (
                    myGuildsQuery.data.length === 0 ? (
                      <p className="text-body-secondary mb-0">You are not a member of any guilds yet.</p>
                    ) : (
                      <div className="list-group list-group-flush border border-secondary-subtle overflow-hidden">
                        {myGuildsQuery.data.map((membership) => (
                          <Link key={membership.id} className="list-group-item list-group-item-action bg-body text-light rounded-0" to={serverGuildPath(membership.guild.server?.slug ?? activeServerSlug, membership.guild)}>
                            <div className="d-flex justify-content-between gap-2">
                              <span className="fw-semibold">{membership.guild.name}</span>
                              <span className="badge text-bg-secondary rounded-0">{membership.role}</span>
                            </div>
                            <div className="small text-body-secondary">{membership.guild.server?.name ?? 'Unknown server'}</div>
                          </Link>
                        ))}
                      </div>
                    )
                  ) : null}
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-7">
              <div className="row g-4">
                <div className="col-12 col-lg-6">
                  <form className="card rankings-card h-100" onSubmit={handleCreateSubmit}>
                    <div className="card-body p-3">
                      <h2 className="h5 mb-3">Create a Guild</h2>
                      <div className="mb-3">
                        <label className="form-label">Server</label>
                        <div className="form-control bg-body-tertiary text-body-secondary" aria-readonly="true">{activeServerName}</div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Guild Name</label>
                        <input className="form-control" value={createForm.name} onChange={(event) => setCreateForm((form) => ({ ...form, name: event.target.value }))} required />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea className="form-control" rows={2} value={createForm.description} onChange={(event) => setCreateForm((form) => ({ ...form, description: event.target.value }))} />
                      </div>
                      <button className="btn btn-success" type="submit" disabled={createMutation.isPending || !createForm.serverId}>Create Guild</button>
                      {!createForm.serverId && !serversQuery.isLoading ? <div className="text-warning small mt-2">Could not resolve the active server.</div> : null}
                      {createMutation.isError ? <div className="text-danger small mt-2">{createMutation.error.message}</div> : null}
                    </div>
                  </form>
                </div>

                <div className="col-12 col-lg-6">
                  <form className="card rankings-card h-100" onSubmit={handleJoinSubmit}>
                    <div className="card-body p-3">
                      <h2 className="h5 mb-3">Join by Invite Code</h2>
                      <p className="text-body-secondary small">Guild invite codes are separate from client upload codes.</p>
                      <div className="mb-3">
                        <label className="form-label">Invite Code</label>
                        <input className="form-control font-monospace" value={joinCode} onChange={(event) => setJoinCode(event.target.value)} required />
                      </div>
                      <button className="btn btn-outline-light" type="submit" disabled={joinMutation.isPending}>Join Guild</button>
                      {joinMutation.isError ? <div className="text-danger small mt-2">{joinMutation.error.message}</div> : null}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="card rankings-card p-3 mb-4">
            Log in with Discord to create a guild or join one with an invite code.
          </div>
        )}

        {message ? <div className="alert alert-success border-success-subtle rounded-0">{message}</div> : null}

        {guildsQuery.isLoading ? <LoadingState label="Loading guilds" /> : null}
        {guildsQuery.isError ? <ErrorState title="Could not load guilds" error={guildsQuery.error} /> : null}

        {guildsQuery.data ? (
          <section className="card rankings-card rankings-table-card">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
                <div>
                  <h2 className="rankings-card-title mb-1">Guild Directory</h2>
                  <div className="small text-body-secondary">{filteredGuilds.length} guild{filteredGuilds.length === 1 ? '' : 's'} on {activeServerName}</div>
                </div>
                <div className="guild-search-field">
                  <label className="form-label small text-uppercase text-body-secondary mb-1">Search Guilds</label>
                  <input
                    className="form-control rounded-0"
                    placeholder="Search guild name or description..."
                    value={guildSearch}
                    onChange={(event) => setGuildSearch(event.target.value)}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-dark table-hover align-middle rankings-mini-table mb-0">
                  <thead>
                    <tr>
                      <th>Guild</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleGuilds.length === 0 ? (
                      <tr><td colSpan={2} className="text-center text-body-secondary py-4">No guilds found.</td></tr>
                    ) : visibleGuilds.map((guild) => (
                      <tr key={guild.id}>
                        <td>
                          <Link className="link-light fw-semibold text-decoration-none" to={serverGuildPath(activeServerSlug, guild)}>{guild.name}</Link>
                        </td>
                        <td className="text-body-secondary">{guild.description || 'No description provided.'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 ? (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                  <div className="small text-body-secondary">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="btn-group" role="group" aria-label="Guild directory pagination">
                    <button className="btn btn-outline-light btn-sm rounded-0" type="button" disabled={currentPage <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
                    <button className="btn btn-outline-light btn-sm rounded-0" type="button" disabled={currentPage >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>Next</button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
